<template>
  <div>
    <loader :loading="loading"></loader>
    <div class="row">
      <div class="col-md-6 mb-4">
        <h2>Add Survey User</h2>
        <p v-if="mode !== 1 || (showSurveyUrlId && !showSurveyName)" v-html="surveyTitle"></p>
        <p>Role: {{ roleTitle }}</p>
      </div>
    </div>
    <div v-if="errorMessage.length" class="row">
      <div class="col-md-8">
        <base-alert class="text-center" type="danger" v-html="errorMessage"></base-alert>
      </div>
    </div>
    <validation-observer v-slot="{ handleSubmit }" ref="surveyuser">
      <form @submit.prevent="handleSubmit(addUser)">
        <div class="row">
          <div class="col-md-4 form-group">
            <label>User Name</label>
            <validation-provider rules="requiredUsername" v-slot="{ errors }">
              <el-select
                class="select-primary"
                size="large"
                placeholder="Select User"
                addon-left-icon="tim-icons icon-link-72"
                :error="errors[0]"
                v-model="username">
                <el-option
                  class="select-primary"
                  :value="nullValue"
                  label="">
                </el-option>
                <el-option v-for="u in users" :key="u.username"
                  class="select-primary"
                  :value="u.username"
                  :label="u.username">
                </el-option>
              </el-select>
            </validation-provider>
          </div>
        </div>
        <div class="row">
          <div v-if="mode === 1 && showSurveyName" class="col-md-4 form-group">
            <label>Survey Name</label>
            <validation-provider rules="requiredField" v-slot="{ errors }">
              <el-select
                class="select-primary"
                size="large"
                placeholder="Select Survey Name"
                :error="errors[0]"
                v-model="surveyId">
                <el-option
                  class="select-primary"
                  :value="nullValue"
                  label="">
                </el-option>
                <el-option v-for="s in surveyNames" :key="s.hspSurveyId"
                  class="select-primary"
                  :value="s.hspSurveyId"
                  :label="s.surveyName">
                </el-option>
              </el-select>
            </validation-provider>
          </div>
          <div v-if="mode === 1 && showSurveyUrlId" class="col-md-4 form-group">
            <label>Survey Instance</label>
            <validation-provider rules="requiredField" v-slot="{ errors }">
              <el-select
                class="select-primary"
                size="large"
                placeholder="Select Survey Instance"
                :error="errors[0]"
                v-model="surveyUrlId">
                <el-option
                  class="select-primary"
                  :value="nullValue"
                  label="">
                </el-option>
                <el-option v-for="s in surveyInstances" :key="s.orgSurveyId"
                  class="select-primary"
                  :value="s.surveyUrlId"
                  :label="s.surveyUrlId">
                </el-option>
              </el-select>
            </validation-provider>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4 mt-3">
            <base-button native-type="submit" type="primary" class="mb-3">
              Add {{ addText }}
            </base-button>
          </div>
        </div>
      </form>
    </validation-observer>
  </div>
</template>
<script>
  import Loader from 'src/pages/Survey/Loader';
  import { mapGetters } from 'vuex';
  import { ValidationObserver, ValidationProvider } from 'vee-validate';
  import { BaseAlert } from 'src/components';
  import { Select, Option } from 'element-ui';
  import swal from 'sweetalert2';
  import { getAdminManagerUsers, getAdminUserUsers, getAdminAssignableUsers, addSurveyAdminUser, getRoles } from 'src/modules/adminActions';
  import { isNullOrEmpty } from '../../modules/utils';
import { orgSurveys } from '../../store/getters';

  export default {
    components: {
      Loader,
      [Option.name]: Option,
      [Select.name]: Select,
      ValidationObserver,
      ValidationProvider,
      BaseAlert
    },
    data() {
      return {
        users: [],
        username: null,
        surveyId: null,
        surveyUrlId: null,
        errorMessage: '',
        loading: false,
        roles: [],
        surveyNames: [],
        surveyInstances: [],
        modes: { default: 1, surveyId: 2, surveyUrlId: 3 },
        showSurveyName: true,
        showSurveyUrlId: true,
        roleTitle: "",
        addText: "",
        nullValue: null,
      };
    },
    computed: {
      mode() {
        return this.$route.params.surveyId && this.$route.params.surveyUrlId ? this.modes.surveyUrlId 
          : this.$route.params.surveyId ? this.modes.surveyId
          : this.modes.default;
      },
      surveyNameDisplay() {
        return this.mode === this.modes.surveyId ? this.getSurveyById(this.$route.params.surveyId).surveyName
          : this.$route.params.surveyUrlId;
      },
      surveyTitle() {
        let text = "";
        if (this.$route.params.surveyId) {
          text = "Survey Name: " + this.getSurveyById(this.$route.params.surveyId).surveyName;
          if (this.$route.params.surveyUrlId) {
            text += "<br>Instance: " + this.$route.params.surveyUrlId;
          }
        }
        else if (this.$route.params.surveyUrlId) {
          text = "Survey Instance: " + this.$route.params.surveyUrlId;
        }
        else if (this.showSurveyUrlId) {
          const found = this.surveyInstances.find(x => x.surveyUrlId === this.surveyUrlId);
          text = "Survey Name: ";
          if (found) {
            text += found.surveyName;
          }
        }

        return text;
      },
      orgSurveyId() {
        if (isNullOrEmpty(this.surveyUrlId)) {
          return null;
        }

        return this.getOrgSurveyByUrlId(this.surveyUrlId).orgSurveyId;
      },
      ...mapGetters({
        getSurveyById: 'getSurveyById',
        getOrgSurveyByUrlId: 'getOrgSurveyByUrlId',
        surveys: 'surveys',
        orgSurveys: 'orgSurveys',
      }),
    },
    methods: {
      addUser() {
        this.loading = true;

        this.errorMessage = "";
        let payload = {
          username: this.username,
          surveyUser: {
            Id: 0,
            HspSurveyId: this.mode === this.modes.surveyUrlId || this.roleTitle !== "manager" ? null : this.surveyId,
            OrgSurveyId: this.mode === this.modes.surveyId || this.roleTitle !== "user" ? null : this.orgSurveyId,
            UserId: "",
          }
        };

        addSurveyAdminUser(payload)
        .then(response => {
          let result = response.data;

          this.errorMessage = '';

          if(result.errors && result.errors.length) {
            result.errors.forEach(error => {
              this.errorMessage += `${error}<br>`
            });
          }
          else {
            swal('Survey User Added!', 'Go to the Survey Users route to verify.', 'success');
          }
        })
        .finally(() => {
          this.loading = false;
        })
      },
      sortRoles() {
        this.roles.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      },
      sortObject(obj, name) {
        obj.sort((a, b) => a[name].toLowerCase().localeCompare(b[name].toLowerCase()));
      },
      setDefaults() {
        this.showSurveyName = true;
        this.showSurveyUrlId = true;
        this.surveyId = null;
        this.surveyUrlId = null;
      },
      resetSurveyNames() {
        this.surveyNames = [...this.surveys];
        this.sortObject(this.surveyNames, "surveyName");
      },
      resetSurveyInstances() {
        this.surveyInstances = [...this.orgSurveys];
        this.sortObject(this.surveyInstances, "surveyUrlId");
      },
      initializeSurveyNames() {
        if (this.mode === this.modes.surveyId) {
          this.surveyNames = [{...this.getSurveyById(this.$route.params.surveyId)}];
          this.surveyId = this.$route.params.surveyId;
        }
        else if (this.mode === this.modes.surveyUrlId) {
          const orgSurvey = this.getOrgSurveyByUrlId(this.$route.params.surveyUrlId);
          this.surveyNames = [{...this.getSurveyById(orgSurvey.hspSurveyId)}];
          this.surveyId = this.surveyNames[0].hspSurveyId;
          this.surveyUrlId = this.$route.params.surveyUrlId;
        }
        else {
          this.resetSurveyNames();
          this.resetSurveyInstances();
        }
      },
      updateSurveyInstances(surveyId) {
        if (isNullOrEmpty(surveyId)) {
          return;
        }
        this.surveyInstances = this.surveyNames.find(x => x.hspSurveyId === surveyId).orgSurveys;
      },
      updateUsers(data) {
        this.users = data;
        this.users.sort((a, b) => a.username.toLowerCase().localeCompare(b.username.toLowerCase()));
      },
      getUsers() {
        this.addText = "User";
        if (this.mode === this.modes.surveyUrlId) {
          getAdminUserUsers()
            .then((response) => {
              this.updateUsers(response.data);
              this.roleTitle = "user";
              this.addText = "to Survey Instance";
            });
        }
        else if (this.mode === this.modes.surveyId) {
          getAdminManagerUsers()
            .then((response) => {
              this.updateUsers(response.data);
              this.roleTitle = "manager";
              this.addText = "to Survey";
            });
        }
        else {
          getAdminAssignableUsers()
            .then((response) => {
              this.updateUsers(response.data);
            });
        }
      },
      onUsernameChange(name, oldName) {
        if (this.mode === this.modes.default) {
          this.addText = "User";
          this.roleTitle = "";
        }
        const found = this.users.find(x => x.username === name);
        if (found) {
          let oldValue = this.users.find(x => x.username === oldName);
          let oldRole = [];
          if (oldValue) {
            oldRole = oldValue.roles.map(x => x.name).join(',');
          }
          let role = found.roles.map(x => x.name).join(',');
          if (role.indexOf("manager") > -1) {
            this.showSurveyName = true;
            this.showSurveyUrlId = false;
            if (this.mode === this.modes.default) {
              this.roleTitle = "manager";
              this.addText = "User to Survey";
            }

            // If user doesn't have the same role as the previously selected user, reset the 'user' related value 'surveyUrlId'
            if (oldRole.length > 0 && oldRole.indexOf("manager") === -1) {
              this.surveyUrlId = null;
            }
          }
          else if (role.indexOf("user") > -1) {
            this.showSurveyUrlId = true;
            this.showSurveyName = false;
            if (this.mode === this.modes.default) {
              this.roleTitle = "user";
              this.addText = "User to Survey Instance";
            }

            // If user doesn't have the same role as the previously selected user, reset the 'manager' related survey value 'surveyId'
            if (oldRole.length > 0 && oldRole.indexOf("user") === -1) {
              this.surveyId = null;
            }
          }
        }
        else if (this.mode === this.modes.default) {
          this.setDefaults();
        }
      },
    },
    watch: {
      username: function (newValue, oldValue) {
        this.onUsernameChange(newValue, oldValue);
      },
    },
    created() {
      var params = this.$route.params;
      this.initializeSurveyNames();
      this.getUsers();
      getRoles()
        .then((response) => {
          this.roles = response.data.filter(x => x.name !== 'administrator' && x.name !== 'analyst');
          this.sortRoles();
        });
    },
  }
</script>
